/**
 * Microphone Service
 *
 * Manages microphone stream lifecycle to avoid repeated permission prompts on iOS.
 * Keeps a warm stream reference that can be reused across recording sessions.
 */

let cachedStream = null;
let cachedDeviceId = null;
let permissionState = 'unknown'; // 'granted', 'denied', 'prompt', 'unknown'

/**
 * Get list of available audio input devices.
 * Note: Device labels are only available after permission is granted.
 * @returns {Promise<Array<{deviceId: string, label: string}>>}
 */
export const getInputDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices
      .filter(device => device.kind === 'audioinput')
      // Filter out "default" virtual device - we have a "Default microphone" option already
      .filter(device => device.deviceId !== 'default' && !device.label.toLowerCase().startsWith('default'))
      .map(device => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`
      }));
    return audioInputs;
  } catch (err) {
    console.error('Failed to enumerate devices:', err);
    return [];
  }
};

/**
 * Request microphone permission and cache the stream.
 * On iOS, this avoids repeated permission prompts by keeping the stream alive.
 * @param {string} [deviceId] - Optional specific device ID to use
 * @returns {Promise<MediaStream>}
 */
export const getMicrophoneStream = async (deviceId = null) => {
  // If we have a cached stream with active tracks for the same device, return it
  if (cachedStream && cachedDeviceId === deviceId) {
    const tracks = cachedStream.getAudioTracks();
    if (tracks.length > 0 && tracks[0].readyState === 'live') {
      console.log('Returning cached microphone stream');
      return cachedStream;
    }
    // Stream is dead, clear the cache
    cachedStream = null;
    cachedDeviceId = null;
  }

  // If switching devices, release the old stream first
  if (cachedStream && cachedDeviceId !== deviceId) {
    console.log('Switching microphone device, releasing old stream');
    releaseStream();
  }

  // Build audio constraints
  const audioConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    sampleRate: 44100,
  };

  // Add device constraint if specified
  if (deviceId) {
    audioConstraints.deviceId = { exact: deviceId };
  }

  // Request new stream
  console.log('Requesting new microphone stream', deviceId ? `for device: ${deviceId}` : '(default)');
  console.log('Audio constraints:', JSON.stringify(audioConstraints));

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: audioConstraints
  });

  // Log stream details for debugging
  const tracks = stream.getAudioTracks();
  if (tracks.length > 0) {
    const track = tracks[0];
    console.log('Microphone stream acquired:', {
      trackId: track.id,
      label: track.label,
      readyState: track.readyState,
      muted: track.muted,
      enabled: track.enabled,
      settings: track.getSettings ? track.getSettings() : 'N/A'
    });
  }

  // Cache the stream and device ID
  cachedStream = stream;
  cachedDeviceId = deviceId;
  permissionState = 'granted';

  // Listen for track ending (user revokes permission, etc.)
  stream.getAudioTracks().forEach(track => {
    track.onended = () => {
      console.log('Microphone track ended');
      cachedStream = null;
      cachedDeviceId = null;
    };
  });

  return stream;
};

/**
 * Check if we have an active microphone stream
 * @returns {boolean}
 */
export const hasActiveStream = () => {
  if (!cachedStream) return false;
  const tracks = cachedStream.getAudioTracks();
  return tracks.length > 0 && tracks[0].readyState === 'live';
};

/**
 * Get the current input device name from the active stream
 * @returns {string|null} Device label or null if no active stream
 */
export const getInputDeviceName = () => {
  if (!cachedStream) return null;
  const tracks = cachedStream.getAudioTracks();
  if (tracks.length === 0) return null;
  return tracks[0].label || 'Default Microphone';
};

/**
 * Get the current cached device ID
 * @returns {string|null} Device ID or null if using default
 */
export const getCurrentDeviceId = () => cachedDeviceId;

/**
 * Get the current permission state (if known)
 * @returns {string} 'granted', 'denied', 'prompt', or 'unknown'
 */
export const getPermissionState = () => permissionState;

/**
 * Check microphone permission status using Permissions API
 * Note: Not supported on all browsers, especially iOS Safari
 * @returns {Promise<string>} 'granted', 'denied', 'prompt', or 'unknown'
 */
export const checkPermission = async () => {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({ name: 'microphone' });
      permissionState = result.state;
      return result.state;
    }
  } catch (e) {
    // Permissions API not supported (common on iOS Safari)
    console.log('Permissions API not supported');
  }
  return 'unknown';
};

/**
 * Release the cached microphone stream.
 * Call this when the app is backgrounded or user logs out.
 */
export const releaseStream = () => {
  if (cachedStream) {
    console.log('Releasing microphone stream');
    cachedStream.getTracks().forEach(track => track.stop());
    cachedStream = null;
    cachedDeviceId = null;
  }
};

/**
 * Pre-warm the microphone by requesting permission early.
 * Call this on app load to prompt for permission once.
 * @returns {Promise<boolean>} true if permission granted
 */
export const warmupMicrophone = async () => {
  try {
    await getMicrophoneStream();
    return true;
  } catch (err) {
    console.log('Microphone warmup failed:', err.name);
    if (err.name === 'NotAllowedError') {
      permissionState = 'denied';
    }
    return false;
  }
};
